import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, House, Notebook, ChartLine, User, UsersThree,
  Copy, Check, Users, Money, Link as LinkIcon, Share, WhatsappLogo, ArrowRight
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const Affiliates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [affiliateData, setAffiliateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const fetchAffiliateData = async () => {
    try {
      const response = await axios.get(`${API}/affiliates/dashboard`);
      setAffiliateData(response.data);
    } catch (error) {
      console.error('Affiliate data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(affiliateData?.referral_link || '');
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Nzadi Fit - Constrói o corpo dos teus sonhos',
          text: 'Junta-te ao Nzadi Fit e começa a transformação!',
          url: affiliateData?.referral_link
        });
      } catch (error) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  const handleWithdrawRequest = useCallback(async () => {
    const amount = parseInt(withdrawAmount.replace(/\D/g, ''));
    
    if (!amount || amount < affiliateData?.min_withdrawal) {
      toast.error(`Valor mínimo: ${affiliateData?.min_withdrawal_formatted}`);
      return;
    }

    if (amount > affiliateData?.available_balance) {
      toast.error(`Saldo insuficiente. Disponível: ${affiliateData?.earnings_formatted}`);
      return;
    }

    setWithdrawing(true);
    try {
      const response = await axios.post(`${API}/affiliates/withdraw`, { amount });
      
      toast.success("Pedido de saque registrado!");
      
      // Open WhatsApp with pre-filled message
      if (response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
      }
      
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      fetchAffiliateData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao solicitar saque");
    } finally {
      setWithdrawing(false);
    }
  }, [withdrawAmount, affiliateData]);

  const formatAmount = (value) => {
    const num = parseInt(value.replace(/\D/g, ''));
    if (!num) return '';
    return num.toLocaleString('pt');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-primary font-bold text-xl">Nzadi Fit</div>
        </div>
      </div>
    );
  }

  const canWithdraw = affiliateData?.can_withdraw;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center gap-4">
        <button 
          onClick={() => navigate('/')}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} weight="bold" />
        </button>
        <h1 className="text-xl font-bold">Painel de Afiliados</h1>
      </header>

      <main className="p-4 space-y-4">
        {/* Earnings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary text-white rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <Money size={40} weight="duotone" />
              <p className="text-white/80 text-sm mt-2">Saldo Disponível</p>
              <p className="text-4xl font-bold font-mono">{affiliateData?.earnings_formatted}</p>
            </div>
            {canWithdraw && (
              <button
                onClick={() => setShowWithdrawModal(true)}
                className="bg-white text-primary font-bold px-4 py-3 rounded-xl flex items-center gap-2 hover:bg-white/90 transition-colors"
                data-testid="withdraw-button"
              >
                <WhatsappLogo size={20} weight="fill" />
                Solicitar Levantamento
              </button>
            )}
          </div>
          
          {!canWithdraw && (
            <div className="bg-white/10 rounded-xl p-3 mt-2">
              <p className="text-white/80 text-sm">
                📊 Mínimo para saque: <span className="font-bold">{affiliateData?.min_withdrawal_formatted}</span>
              </p>
              <p className="text-white/60 text-xs mt-1">
                Faltam {((affiliateData?.min_withdrawal || 10000) - (affiliateData?.available_balance || 0)).toLocaleString('pt')} Kz
              </p>
            </div>
          )}
          
          <p className="text-white/70 text-sm mt-4">
            Ganha {affiliateData?.commission_per_referral} por cada aluno que assinar
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-4 shadow-card text-center"
          >
            <Users size={24} className="text-primary mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono text-foreground">{affiliateData?.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Total Referidos</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-4 shadow-card text-center"
          >
            <Check size={24} className="text-primary mx-auto mb-2" weight="duotone" />
            <p className="text-2xl font-bold font-mono text-foreground">{affiliateData?.active_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Ativos (pagantes)</p>
          </motion.div>
        </div>

        {/* Referral Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-5 shadow-card"
        >
          <h3 className="font-bold text-foreground mb-4">O teu Código</h3>
          <div className="bg-muted/50 rounded-xl p-4 text-center mb-4">
            <p className="text-3xl font-bold font-mono text-primary tracking-wider">
              {affiliateData?.referral_code}
            </p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={copyLink}
              className="flex-1 bg-primary/10 text-primary font-medium py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-primary/20 transition-colors"
              data-testid="copy-link-button"
            >
              {copied ? <Check size={20} weight="bold" /> : <Copy size={20} />}
              {copied ? "Copiado!" : "Copiar Link"}
            </button>
            <button
              onClick={shareLink}
              className="flex-1 bg-primary text-white font-medium py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-primary-hover transition-colors"
              data-testid="share-link-button"
            >
              <Share size={20} />
              Partilhar
            </button>
          </div>
        </motion.div>

        {/* Referral Link Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-4 shadow-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <LinkIcon size={20} className="text-primary" weight="duotone" />
            <span className="text-sm text-muted-foreground">Link de Afiliado</span>
          </div>
          <p className="text-sm font-mono text-foreground bg-muted/50 p-3 rounded-lg break-all">
            {affiliateData?.referral_link}
          </p>
        </motion.div>

        {/* Referrals List */}
        {affiliateData?.referrals?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-card overflow-hidden"
          >
            <h3 className="font-bold text-foreground p-4 bg-muted/30">Meus Afilhados</h3>
            <div className="divide-y divide-muted">
              {affiliateData.referrals.map((ref, index) => (
                <div key={index} className="flex items-center justify-between p-4">
                  <div>
                    <p className="font-medium text-foreground">{ref.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(ref.date).toLocaleDateString('pt')}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    ref.active 
                      ? 'bg-primary/10 text-primary' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {ref.active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-muted/50 rounded-2xl p-5"
        >
          <h3 className="font-bold text-foreground mb-4">Como funciona?</h3>
          <ol className="space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
              <span>Partilha o teu link com amigos interessados em fitness</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
              <div>
                <span>Quando eles assinarem, ganhas comissão:</span>
                <ul className="mt-1 ml-2 space-y-1">
                  <li>• Mensal: <strong className="text-primary">2.000 Kz</strong></li>
                  <li>• Semestral: <strong className="text-primary">6.000 Kz</strong></li>
                  <li>• Anual: <strong className="text-primary">10.000 Kz</strong></li>
                </ul>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
              <span>Acumula mínimo <strong className="text-primary">10.000 Kz</strong> para solicitar levantamento</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
              <span>Pagamentos são feitos <strong className="text-primary">semanalmente</strong> via Multicaixa Express</span>
            </li>
          </ol>
        </motion.div>
      </main>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="bg-white rounded-3xl border-none max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">Solicitar Levantamento</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Money size={32} className="text-primary" weight="duotone" />
              </div>
              <p className="text-muted-foreground text-sm">Saldo disponível</p>
              <p className="text-2xl font-bold text-primary">{affiliateData?.earnings_formatted}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Valor a levantar (Kz)</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="10.000"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(formatAmount(e.target.value))}
                    className="w-full text-center text-2xl font-bold font-mono bg-muted/50 border-2 border-transparent focus:border-primary rounded-2xl py-4 outline-none transition-all"
                    data-testid="withdraw-amount-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">Kz</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Mínimo: {affiliateData?.min_withdrawal_formatted}
                </p>
              </div>

              <div className="bg-muted/50 rounded-xl p-3 text-sm">
                <p className="text-muted-foreground">
                  <WhatsappLogo size={16} weight="fill" className="inline text-green-600 mr-1" />
                  Ao confirmar, serás redirecionado para o WhatsApp para finalizar o pedido.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => { setShowWithdrawModal(false); setWithdrawAmount(""); }} 
              className="flex-1 rounded-full"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleWithdrawRequest} 
              disabled={withdrawing || !withdrawAmount}
              className="flex-1 btn-primary"
            >
              {withdrawing ? "A processar..." : (
                <>
                  <WhatsappLogo size={20} className="mr-2" weight="fill" />
                  Confirmar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-muted p-2 flex justify-around items-center z-50 safe-bottom">
        {[
          { icon: House, label: "Início", path: "/" },
          { icon: Notebook, label: "Diário", path: "/diary" },
          { icon: ChartLine, label: "Progresso", path: "/progress" },
          { icon: UsersThree, label: "Afiliados", path: "/affiliates", active: true },
          { icon: User, label: "Perfil", path: "/profile" }
        ].map(({ icon: Icon, label, path, active }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
              active ? "text-primary" : "text-muted-foreground hover:text-primary"
            }`}
          >
            <Icon size={24} weight={active ? "fill" : "regular"} />
            <span className="text-xs mt-1">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

export default Affiliates;
